/**
 * Client-side geolocation utilities for detecting user country
 */

import { API_BASE_URL } from '../config';

export type CountryDetectionSource = 'cf-header' | 'ip-api' | 'fallback';

export interface CountryDetectionResult {
  countryCode: string | null;
  source: CountryDetectionSource;
}

/**
 * Get country code from Cloudflare header (if behind Cloudflare)
 */
function getCountryFromCloudflarHeader(): string | null {
  try {
    // Cloudflare sets cf-ipcountry header, but it's usually not accessible from client
    // We'll rely on server headers instead
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch country code from server-side geolocation
 */
async function getCountryFromServer(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/geo/country`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { country?: string };
    const country = String(data.country || '').trim().toLowerCase();
    if (country && country.length === 2) return country;
    return null;
  } catch {
    return null;
  }
}

/**
 * Detect user's country code using multiple strategies
 */
export async function detectCountryCode(): Promise<CountryDetectionResult> {
  // Try Cloudflare first (fastest, no network call)
  const cfCountry = getCountryFromCloudflarHeader();
  if (cfCountry) return { countryCode: cfCountry, source: 'cf-header' };

  // Try server-side geolocation
  const serverCountry = await getCountryFromServer();
  if (serverCountry) return { countryCode: serverCountry, source: 'ip-api' };

  // Fallback to Germany (main market)
  return { countryCode: 'de', source: 'fallback' };
}

/**
 * Cache country code in localStorage
 */
export function cacheCountryCode(code: string): void {
  try {
    localStorage.setItem('pickitup_country_code', code);
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Get cached country code from localStorage
 */
export function getCachedCountryCode(): string | null {
  try {
    return localStorage.getItem('pickitup_country_code');
  } catch {
    return null;
  }
}
